/**
 * 学习乐园 · 一闪而过（game-yishan.js）
 * ============================================================
 * 「记忆与观察」组第三作：网格上几个格子亮一下熄灭，凭记忆按原位点回来。
 * 纯观察记忆（空间位置），零内容数据——不依赖任何题材包。
 *
 * 档位（默认档铁律——最简）：
 *   入门 4×4 亮 3 格，亮 2.2 秒（默认档）/ 进阶 5×5 亮 5 格 1.8 秒 /
 *   挑战 6×6 亮 7 格 1.5 秒。
 *
 * 流程：亮灯阶段（is-lit，按钮禁用）→ 点回阶段（点对保持点亮，点错闪红讲原因）
 * → 找齐全部亮格即完成。没有失败态——错点只计数不重来，页面明说「这格没亮过」。
 * 「再亮一眼」把还没找到的亮格再闪 0.8 秒（不限次数但每次如实计入状态）。
 *
 * 记录：按档位记完成局数 / 最少错点 / 最好用时，只存本机 localStorage。
 *
 * 冒烟钩子：window.STYishan 暴露 makeTargets / state()；fixRandom 下
 * makeTargets 取前 k 格（Fisher–Yates 恒 k=0 不换位），冒烟可确定性通关。
 */
(function (global) {
  'use strict';

  var PREF_KEY = 'study-yishan';
  var RECORD_KEY = 'study-yishan-record';
  var BAD_MS = 420;    // 点错红闪时长
  var PEEK_MS = 800;   // 「再亮一眼」的亮出时长

  /* ============ 引擎：出题（纯函数） ============ */

  function shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = arr[i]; arr[i] = arr[j]; arr[j] = t;
    }
    return arr;
  }

  // size×size 网格里抽 k 个不同格号（升序返回，便于比对与讲解）
  function makeTargets(size, k) {
    var pool = [];
    for (var i = 0; i < size * size; i++) pool.push(i);
    shuffle(pool);
    return pool.slice(0, Math.min(k, pool.length)).sort(function (a, b) { return a - b; });
  }

  /* ============ UI：挂载与状态 ============ */

  var LEVELS = [
    { size: 4, lit: 3, showMs: 2200, name: '入门 4×4', hint: '默认档 · 亮 3 格' },
    { size: 5, lit: 5, showMs: 1800, name: '进阶 5×5', hint: '亮 5 格' },
    { size: 6, lit: 7, showMs: 1500, name: '挑战 6×6', hint: '亮 7 格' }
  ];

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  var root = null;
  var ui = {};
  var S = {
    soundOn: true,
    size: 4, lit: 3, showMs: 2200,
    targets: [],         // 亮过的格号集合（升序）
    hits: [],            // 已点中的格号
    misses: 0, hints: 0,
    phase: 'show',       // show（亮灯）| recall（点回）| ended
    showStart: 0,        // 亮灯开始时刻（状态行倒数用）
    elapsed: 0, timer: null,
    showTimer: null, badTimer: null, peekTimer: null,
    ended: false,
    resetArm: false, resetTimer: null
  };

  /* ---- 档位选择器 ---- */

  function renderLevelButtons() {
    var box = ui.levels;
    box.textContent = '';
    for (var i = 0; i < LEVELS.length; i++) {
      (function (lv) {
        var b = el('button', 'st-llk-pack-btn');
        b.type = 'button';
        b.setAttribute('data-level', String(lv.size));
        b.setAttribute('role', 'radio');
        b.appendChild(el('span', 'st-llk-pack-name', lv.name));
        b.appendChild(el('small', 'st-llk-pack-hint', lv.hint));
        b.addEventListener('click', function () {
          if (S.size === lv.size) return;
          S.size = lv.size; S.lit = lv.lit; S.showMs = lv.showMs;
          newGame();   // 换档 = 直接开新局
        });
        box.appendChild(b);
      })(LEVELS[i]);
    }
  }

  function syncLevelButtons() {
    var btns = ui.levels.querySelectorAll('[data-level]');
    for (var i = 0; i < btns.length; i++) {
      var on = Number(btns[i].getAttribute('data-level')) === S.size;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  /* ---- 棋盘 ---- */

  function buildBoard() {
    var board = ui.board;
    board.textContent = '';
    board.style.setProperty('--st-ys-cols', String(S.size));
    ui.cells = [];
    for (var i = 0; i < S.size * S.size; i++) {
      (function (idx) {
        var b = el('button', 'st-ys-cell');
        b.type = 'button';
        b.setAttribute('data-idx', String(idx));
        b.addEventListener('click', function () { onCell(idx); });
        board.appendChild(b);
        ui.cells.push(b);
      })(i);
    }
    renderCells();
  }

  function renderCells() {
    for (var i = 0; i < ui.cells.length; i++) {
      var btn = ui.cells[i];
      var isTarget = S.targets.indexOf(i) >= 0;
      var isHit = S.hits.indexOf(i) >= 0;
      var label = '第' + (Math.floor(i / S.size) + 1) + '行第' + (i % S.size + 1) + '列';
      if (S.phase === 'show' && isTarget) label += '，亮着';
      else if (isHit) label += '，已找回';
      else if (S.phase === 'recall') label += '，未点';
      btn.setAttribute('aria-label', label);
      btn.classList.toggle('is-lit', S.phase === 'show' && isTarget);
      btn.classList.toggle('is-hit', isHit);
      btn.classList.toggle('is-bad', btn.__bad === true);
      // 亮灯阶段与已找回的格子不可再点
      btn.disabled = S.phase !== 'recall' || isHit;
    }
  }

  /* ---- 渲染：状态 / 反馈 / 成绩 / 按钮 ---- */

  function fmtTime(sec) {
    var m = Math.floor(sec / 60), s = sec % 60;
    return m + ' 分 ' + (s < 10 ? '0' : '') + s + ' 秒';
  }
  function fmtTimeShort(sec) {
    var m = Math.floor(sec / 60), s = sec % 60;
    return m ? m + ' 分 ' + (s < 10 ? '0' : '') + s + ' 秒' : s + ' 秒';
  }

  function remainingShow() {
    if (!S.showStart) return S.showMs;
    var left = S.showMs - (Date.now() - S.showStart);
    return left > 0 ? left : 0;
  }

  function renderStatus() {
    if (S.ended) return;
    ui.status.className = 'st-status';
    if (S.phase === 'show') {
      var left = Math.max(1, Math.ceil(remainingShow() / 1000));
      ui.status.textContent = '亮灯中：记住这 ' + S.targets.length + ' 个格子（约 ' + left + ' 秒后熄灭）';
    } else {
      ui.status.textContent = '找回 ' + S.hits.length + '/' + S.targets.length
        + ' · 错点 ' + S.misses
        + (S.hints ? ' · 再亮 ' + S.hints : '')
        + ' · 用时 ' + fmtTime(S.elapsed);
    }
  }

  function renderNote(text, tone) {
    ui.note.textContent = text || '';
    ui.note.className = 'st-llk-note' + (tone ? ' is-' + tone : '');
  }

  function recKey() { return 'x' + S.size; }

  function renderRecord() {
    var box = ui.record;
    box.textContent = '';
    var rec = loadRecord()[recKey()];
    if (!rec || !rec.wins) {
      box.className = 'st-llk-record is-empty';
      box.appendChild(el('p', 'st-llk-record-empty',
        '这一档还没完成过——凭记忆找齐亮格一次，这里记下你的最少错点。'));
      return;
    }
    box.className = 'st-llk-record';
    box.appendChild(recordStat('完成', rec.wins + ' 局'));
    box.appendChild(recordStat('最少错点', rec.bestMisses + ' 次'));
    box.appendChild(recordStat('最好用时', rec.bestTime ? fmtTimeShort(rec.bestTime) : '—'));
  }

  function recordStat(label, value) {
    var d = el('div', 'st-llk-stat');
    d.appendChild(el('span', 'st-llk-stat-label', label));
    d.appendChild(el('strong', 'st-llk-stat-value', value));
    return d;
  }

  function renderButtons() {
    ui.newBtn.disabled = false;
    ui.peekBtn.disabled = S.ended || S.phase !== 'recall' || S.hits.length >= S.targets.length;
    ui.resetBtn.disabled = false;
    ui.resetBtn.classList.toggle('is-arm', S.resetArm);
    ui.resetBtn.textContent = S.resetArm ? '再点一次确认重置' : '重置成绩';
  }

  /* ---- 音效（WebAudio 本地合成，与点灯同一套音色表） ---- */

  /* 手机外放响度补偿（口径统一，由 scripts/boost-sfx-mobile.mjs 批量写入）：
     手机小喇叭对 500Hz 以下钝感、超短音感知响度低——触屏设备把提示音增益翻倍、
     低于 500Hz 的音移高八度、衰减拉长；桌面/耳机维持原响度。 */
  var SFX_MOBILE = false;
  try {
    SFX_MOBILE = (global.matchMedia && global.matchMedia('(pointer: coarse)').matches) ||
      (global.navigator && global.navigator.maxTouchPoints > 0);
  } catch (e) { /* 检测不可用按桌面处理 */ }
  var actx = null;
  function sfx(kind) {
    if (!S.soundOn) return;
    try {
      var AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) return;
      if (!actx) actx = new AC();
      if (actx.state === 'suspended' && actx.resume) actx.resume();
      var t = actx.currentTime;
      var seq;
      if (kind === 'ok') seq = [[523, 0], [659, 0.07]];
      else if (kind === 'win') seq = [[523, 0], [659, 0.1], [784, 0.2], [1046, 0.3]];
      else if (kind === 'bad') seq = [[196, 0], [185, 0.09]];
      else seq = [[392, 0]];   // lit
      for (var i = 0; i < seq.length; i++) {
        var o = actx.createOscillator();
        var g = actx.createGain();
        o.type = 'triangle';
        o.frequency.setValueAtTime(SFX_MOBILE && seq[i][0] < 500 ? seq[i][0] * 2 : seq[i][0], t + seq[i][1]);
        g.gain.setValueAtTime(0.0001, t + seq[i][1]);
        g.gain.exponentialRampToValueAtTime(SFX_MOBILE ? 0.42 : 0.18, t + seq[i][1] + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + seq[i][1] + (SFX_MOBILE ? 0.2 : 0.14));
        o.connect(g); g.connect(actx.destination);
        o.start(t + seq[i][1]); o.stop(t + seq[i][1] + (SFX_MOBILE ? 0.26 : 0.16));
      }
    } catch (e) { /* 音频不可用静默 */ }
  }

  /* ---- 计时（从点回阶段起算：亮灯是观察时间，不计用时） ---- */

  function startTimer() {
    stopTimer();
    S.elapsed = 0;
    S.timer = setInterval(function () {
      if (S.ended || S.phase !== 'recall') return;
      S.elapsed++;
      renderStatus();
    }, 1000);
  }
  function stopTimer() {
    if (S.timer) { clearInterval(S.timer); S.timer = null; }
  }

  /* ---- 交互：点回 ---- */

  function onCell(idx) {
    if (S.ended || S.phase !== 'recall') return;
    if (S.hits.indexOf(idx) >= 0) return;
    var tIdx = S.targets.indexOf(idx);
    if (tIdx >= 0) {
      // 点对：保持点亮，讲进度
      S.hits.push(idx);
      sfx('ok');
      var left = S.targets.length - S.hits.length;
      renderCells();
      renderButtons();
      renderNote(left
        ? '对了，找回 ' + S.hits.length + '/' + S.targets.length + ' 个——还剩 ' + left + ' 个。'
        : '', 'ok');
      if (!left) { winGame(); return; }
      renderStatus();
    } else {
      // 点错：闪红讲原因，不惩罚不重来
      S.misses++;
      sfx('bad');
      var btn = ui.cells[idx];
      btn.__bad = true;
      renderCells();
      renderNote('这格刚才没亮过。亮过的是 ' + S.targets.length + ' 个位置——回想亮灯那几秒它们分布在哪里（角落？中间？成一条线？）。', 'bad');
      renderStatus();
      if (S.badTimer) clearTimeout(S.badTimer);
      S.badTimer = setTimeout(function () {
        S.badTimer = null;
        btn.__bad = false;
        btn.classList.remove('is-bad');
      }, BAD_MS);
    }
  }

  /* ---- 再亮一眼：没找回的亮格再闪 0.8 秒（次数如实计入） ---- */

  function peek() {
    if (S.ended || S.phase !== 'recall') return;
    if (S.hits.length >= S.targets.length) return;
    S.hints++;
    for (var i = 0; i < S.targets.length; i++) {
      var idx = S.targets[i];
      if (S.hits.indexOf(idx) < 0) ui.cells[idx].classList.add('is-peek');
    }
    renderNote('没找回的亮格又闪了 ' + PEEK_MS / 1000 + ' 秒——趁现在把它们钉在脑子里。亮的次数会如实计入。', 'warn');
    renderStatus();
    renderButtons();
    if (S.peekTimer) clearTimeout(S.peekTimer);
    S.peekTimer = setTimeout(function () {
      S.peekTimer = null;
      for (var j = 0; j < ui.cells.length; j++) ui.cells[j].classList.remove('is-peek');
    }, PEEK_MS);
  }

  /* ---- 完成与成绩（只存本机） ---- */

  function winGame() {
    S.ended = true;
    S.phase = 'ended';
    stopTimer();
    sfx('win');
    var rec = loadRecord();
    var key = recKey();
    var cur = rec[key];
    if (!cur || typeof cur !== 'object' || Array.isArray(cur)) cur = { wins: 0, bestMisses: 0, bestTime: 0 };
    if (typeof cur.wins !== 'number') cur.wins = 0;
    if (typeof cur.bestMisses !== 'number') cur.bestMisses = 0;
    if (typeof cur.bestTime !== 'number') cur.bestTime = 0;
    cur.wins++;
    var isRec = false;
    if (!cur.bestMisses || S.misses < cur.bestMisses) { cur.bestMisses = S.misses; isRec = true; }
    if (!cur.bestTime || S.elapsed < cur.bestTime) cur.bestTime = S.elapsed;
    rec[key] = cur;
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(rec)); } catch (e) { /* 隐私模式静默 */ }
    ui.status.className = 'st-status is-over';
    ui.status.textContent = '完成！' + S.size + '×' + S.size + ' 找齐 ' + S.targets.length
      + ' 个亮格，错点 ' + S.misses + ' 次' + (isRec ? '——这一档的新纪录！' : '（你这档最少错点 ' + cur.bestMisses + ' 次）');
    renderNote('空间记忆的诀窍是「组块化」：别一格一格记，把亮格看成形状——一条斜线、一个角、一坨——一个形状只占一段记忆。', 'ok');
    renderRecord();
    renderButtons();
  }

  function loadRecord() {
    try {
      var r = JSON.parse(localStorage.getItem(RECORD_KEY) || '{}');
      return r && typeof r === 'object' && !Array.isArray(r) ? r : {};
    } catch (e) { return {}; }
  }

  function resetRecord() {
    if (!S.resetArm) {
      S.resetArm = true;
      renderButtons();
      if (S.resetTimer) clearTimeout(S.resetTimer);
      S.resetTimer = setTimeout(function () { S.resetArm = false; S.resetTimer = null; renderButtons(); }, 3000);
      return;
    }
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    try { localStorage.removeItem(RECORD_KEY); } catch (e) { /* 隐私模式静默 */ }
    renderRecord();
    renderButtons();
    toast('已重置一闪而过的本机成绩');
  }

  function save() {
    try {
      localStorage.setItem(PREF_KEY, JSON.stringify({ v: 1, size: S.size, soundOn: S.soundOn }));
    } catch (e) { /* 存储不可用静默 */ }
  }

  function restorePrefs() {
    try {
      var d = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
      if (!d || d.v !== 1) return;
      for (var i = 0; i < LEVELS.length; i++) {
        if (LEVELS[i].size === d.size) {
          S.size = LEVELS[i].size; S.lit = LEVELS[i].lit; S.showMs = LEVELS[i].showMs;
          break;
        }
      }
      S.soundOn = d.soundOn !== false;
    } catch (e) { /* 忽略脏数据 */ }
  }

  function newGame() {
    if (S.showTimer) { clearTimeout(S.showTimer); S.showTimer = null; }
    if (S.badTimer) { clearTimeout(S.badTimer); S.badTimer = null; }
    if (S.peekTimer) { clearTimeout(S.peekTimer); S.peekTimer = null; }
    S.targets = makeTargets(S.size, S.lit);
    S.hits = [];
    S.misses = 0; S.hints = 0;
    S.phase = 'show';
    S.ended = false;
    S.elapsed = 0;
    S.showStart = Date.now();
    S.resetArm = false;
    syncLevelButtons();
    buildBoard();
    renderStatus();
    renderButtons();
    renderRecord();
    renderNote('亮灯中——只亮这 ' + S.targets.length + ' 格、只亮 ' + (S.showMs / 1000)
      + ' 秒。别一格一格死记，先整体扫一眼它们分布在哪里，熄了就凭印象点回去。', 'warn');
    startTimer();
    S.showTimer = setTimeout(function () {
      S.showTimer = null;
      if (S.ended) return;
      S.phase = 'recall';
      S.elapsed = 0;
      renderCells();
      renderStatus();
      renderButtons();
      renderNote('熄了。按原位把 ' + S.targets.length + ' 个亮格点回来——点错不惩罚，页面会告诉你错在哪。', '');
    }, S.showMs);
    save();
  }

  function bindSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        b.addEventListener('click', function () {
          S.soundOn = b.getAttribute('data-st-sound') === 'on';
          for (var j = 0; j < btns.length; j++) {
            var on = btns[j] === b;
            btns[j].classList.toggle('is-active', on);
            btns[j].setAttribute('aria-checked', on ? 'true' : 'false');
          }
          save();
        });
      })(btns[i]);
    }
    syncSoundPicker();
  }

  function syncSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      var on = (btns[i].getAttribute('data-st-sound') === 'on') === S.soundOn;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  function toast(msg) {
    if (!ui.saved) return;
    ui.saved.textContent = msg;
    ui.saved.hidden = false;
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { ui.saved.hidden = true; }, 2200);
  }

  /* ============ boot ============ */

  function boot() {
    root = document.querySelector('[data-st-game]');
    if (!root) return;
    ui.levels = root.querySelector('[data-st-ys-levels]');
    ui.board = root.querySelector('[data-st-ys-board]');
    ui.status = root.querySelector('[data-st-status]');
    ui.note = root.querySelector('[data-st-note]');
    ui.record = root.querySelector('[data-st-record]');
    ui.saved = root.querySelector('[data-st-saved]');
    ui.newBtn = root.querySelector('[data-st-new]');
    ui.peekBtn = root.querySelector('[data-st-peek]');
    ui.resetBtn = root.querySelector('[data-st-reset]');
    if (!ui.board) return;

    restorePrefs();
    renderLevelButtons();
    bindSoundPicker();

    ui.newBtn.addEventListener('click', function () { newGame(); });
    ui.peekBtn.addEventListener('click', peek);
    ui.resetBtn.addEventListener('click', resetRecord);

    newGame();
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  /* ============ 冒烟与复用钩子 ============ */

  global.STYishan = {
    makeTargets: makeTargets,
    LEVELS: LEVELS,
    state: function () {
      return {
        size: S.size, lit: S.lit, showMs: S.showMs, targets: S.targets.slice(), hits: S.hits.slice(),
        misses: S.misses, hints: S.hints, phase: S.phase, elapsed: S.elapsed, ended: S.ended
      };
    }
  };
})(typeof window !== 'undefined' ? window : globalThis);

